import React, { useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
} from '../utils/webComponents';
import { useNavigation } from '../utils/navigation';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  School,
  AutoAwesome,
  Psychology,
  Analytics,
  CheckCircle,
  ArrowForward,
} from '@mui/icons-material';

const LandingPage = () => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const { theme } = useTheme();

  // No auto-redirect - show landing page

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <View style={[styles.logoCircle, { backgroundColor: theme.primary + '15' }]}>
            <School sx={{ fontSize: 56, color: theme.primary }} />
          </View>
          <Text style={[styles.heroTitle, { color: theme.text }]}>
            SpacedAI
          </Text>
          <Text style={[styles.heroSubtitle, { color: theme.textSecondary }]}>
            Master any subject with scientifically-backed flashcards
          </Text>
          <Text style={[styles.heroDescription, { color: theme.textSecondary }]}>
            Designed for optimal memory retention through neuroscience-based spaced repetition
          </Text>
          <View style={styles.ctaContainer}>
            <TouchableOpacity
              style={[styles.primaryButton, { backgroundColor: theme.primary }]}
              onPress={() => navigate('/register')}
            >
              <Text style={[styles.primaryButtonText, { color: 'white' }]}>
                Sign Up
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.secondaryButton, { borderColor: theme.border }]}
              onPress={() => navigate('/login')}
            >
              <Text style={[styles.secondaryButtonText, { color: theme.text }]}>
                Sign In
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.demoButton]}
              onPress={() => {
                // Set demo mode and navigate to home
                localStorage.setItem('isDemoMode', 'true');
                navigate('/home');
              }}
            >
              <Text style={[styles.demoButtonText, { color: theme.textSecondary }]}>
                Try Demo (No Sign Up)
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Features Section */}
        <View style={styles.section}>
          <View style={styles.featureCard}>
            <View style={[styles.featureIcon, { backgroundColor: theme.accent + '15' }]}>
              <AutoAwesome sx={{ fontSize: 32, color: theme.accent }} />
            </View>
            <Text style={[styles.featureTitle, { color: theme.text }]}>
              AI-Powered Generation
            </Text>
            <Text style={[styles.featureDescription, { color: theme.textSecondary }]}>
              Generate flashcards instantly from any topic or document using advanced AI technology
            </Text>
          </View>

          <View style={styles.featureCard}>
            <View style={[styles.featureIcon, { backgroundColor: theme.primary + '15' }]}>
              <Psychology sx={{ fontSize: 32, color: theme.primary }} />
            </View>
            <Text style={[styles.featureTitle, { color: theme.text }]}>
              Smart Spaced Repetition
            </Text>
            <Text style={[styles.featureDescription, { color: theme.textSecondary }]}>
              Neuroscience-based algorithm that shows cards at the perfect time for maximum memory retention
            </Text>
          </View>

          <View style={styles.featureCard}>
            <View style={[styles.featureIcon, { backgroundColor: theme.success + '15' }]}>
              <Analytics sx={{ fontSize: 32, color: theme.success }} />
            </View>
            <Text style={[styles.featureTitle, { color: theme.text }]}>
              Track Your Progress
            </Text>
            <Text style={[styles.featureDescription, { color: theme.textSecondary }]}>
              Monitor your study streaks, success rates, and learning statistics to stay motivated
            </Text>
          </View>
        </View>

        {/* Neuroscience Section */}
        <View style={styles.section}>
          <View style={[styles.neuroscienceCard, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <View style={[styles.neuroscienceIcon, { backgroundColor: theme.primary + '15' }]}>
              <Psychology sx={{ fontSize: 40, color: theme.primary }} />
            </View>
            <Text style={[styles.neuroscienceTitle, { color: theme.text }]}>
              Backed by Neuroscience
            </Text>
            <Text style={[styles.neuroscienceDescription, { color: theme.textSecondary }]}>
              Our approach is grounded in proven scientific research on how your brain learns and retains information
            </Text>
            
            <View style={styles.benefitsList}>
              <View style={styles.benefitItem}>
                <CheckCircle sx={{ fontSize: 24, color: theme.success }} />
                <View style={styles.benefitContent}>
                  <Text style={[styles.benefitTitle, { color: theme.text }]}>
                    The Spacing Effect
                  </Text>
                  <Text style={[styles.benefitDescription, { color: theme.textSecondary }]}>
                    Your brain learns best when you space reviews over time. Our algorithm activates your hippocampus right before you forget—the perfect time for reinforcement.
                  </Text>
                </View>
              </View>

              <View style={styles.benefitItem}>
                <CheckCircle sx={{ fontSize: 24, color: theme.success }} />
                <View style={styles.benefitContent}>
                  <Text style={[styles.benefitTitle, { color: theme.text }]}>
                    Active Recall
                  </Text>
                  <Text style={[styles.benefitDescription, { color: theme.textSecondary }]}>
                    Every time you recall something, your brain strengthens neural connections through long-term potentiation (LTP), making memories more permanent.
                  </Text>
                </View>
              </View>

              <View style={styles.benefitItem}>
                <CheckCircle sx={{ fontSize: 24, color: theme.success }} />
                <View style={styles.benefitContent}>
                  <Text style={[styles.benefitTitle, { color: theme.text }]}>
                    Minimalist Design
                  </Text>
                  <Text style={[styles.benefitDescription, { color: theme.textSecondary }]}>
                    A clean, distraction-free interface keeps your focus on learning. Less visual clutter means better cognitive performance and faster learning.
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Final CTA */}
        <View style={[styles.section, styles.finalCtaSection]}>
          <View style={[styles.finalCta, { backgroundColor: theme.primary }]}>
            <Text style={[styles.finalCtaTitle, { color: 'white' }]}>
              Ready to Transform Your Learning?
            </Text>
            <Text style={[styles.finalCtaSubtitle, { color: 'rgba(255, 255, 255, 0.9)' }]}>
              Join thousands of students mastering their subjects faster
            </Text>
            <TouchableOpacity
              style={[styles.finalCtaButton, { backgroundColor: 'white' }]}
              onPress={() => navigate('/register')}
            >
              <Text style={[styles.finalCtaButtonText, { color: theme.primary }]}>
                Start Learning Free
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Footer */}
        <View style={[styles.footer, { borderTopColor: theme.border }]}>
          <View style={styles.footerLinks}>
            <TouchableOpacity
              onPress={() => navigation.navigate('PrivacyPolicy')}
              style={styles.footerLink}
            >
              <Text style={[styles.footerLinkText, { color: theme.textSecondary }]}>
                Privacy Policy
              </Text>
            </TouchableOpacity>
            <Text style={[styles.footerSeparator, { color: theme.border }]}>•</Text>
            <TouchableOpacity
              onPress={() => navigation.navigate('TermsOfService')}
              style={styles.footerLink}
            >
              <Text style={[styles.footerLinkText, { color: theme.textSecondary }]}>
                Terms of Service
              </Text>
            </TouchableOpacity>
            <Text style={[styles.footerSeparator, { color: theme.border }]}>•</Text>
            <TouchableOpacity
              onPress={() => navigation.navigate('HelpCenter')}
              style={styles.footerLink}
            >
              <Text style={[styles.footerLinkText, { color: theme.textSecondary }]}>
                Help Center
              </Text>
            </TouchableOpacity>
            <Text style={[styles.footerSeparator, { color: theme.border }]}>•</Text>
            <TouchableOpacity
              onPress={() => navigation.navigate('About')}
              style={styles.footerLink}
            >
              <Text style={[styles.footerLinkText, { color: theme.textSecondary }]}>
                About
              </Text>
            </TouchableOpacity>
          </View>
          <Text style={[styles.footerCopyright, { color: theme.textSecondary }]}>
            © 2025 SpacedAI. All rights reserved.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 80,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  heroSection: {
    paddingTop: 100,
    paddingBottom: 120,
    paddingHorizontal: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 700,
    width: '100%',
    margin: '0 auto',
    textAlign: 'center',
  },
  logoCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 40,
  },
  heroTitle: {
    fontSize: 56,
    fontWeight: '700',
    marginBottom: 24,
    textAlign: 'center',
    letterSpacing: -1,
  },
  heroSubtitle: {
    fontSize: 24,
    fontWeight: '500',
    marginBottom: 16,
    textAlign: 'center',
    lineHeight: 1.4,
  },
  heroDescription: {
    fontSize: 18,
    lineHeight: 1.6,
    textAlign: 'center',
    marginBottom: 48,
    maxWidth: 600,
  },
  ctaContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    alignItems: 'center',
    width: '100%',
  },
  primaryButton: {
    padding: 20,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: 200,
    minHeight: 56,
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
    textAlign: 'center',
  },
  secondaryButton: {
    padding: 20,
    borderRadius: 8,
    borderWidth: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: 'transparent',
    minWidth: 200,
    minHeight: 56,
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
    textAlign: 'center',
  },
  demoButton: {
    padding: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  demoButtonText: {
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.3,
    textAlign: 'center',
    textDecoration: 'underline',
  },
  section: {
    paddingHorizontal: 24,
    paddingVertical: 80,
    maxWidth: 800,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    textAlign: 'center',
  },
  finalCtaSection: {
    marginTop: 40,
  },
  featureCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    marginBottom: 80,
    width: '100%',
  },
  featureIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
  },
  featureTitle: {
    fontSize: 28,
    fontWeight: '600',
    marginBottom: 16,
    textAlign: 'center',
  },
  featureDescription: {
    fontSize: 18,
    lineHeight: 1.7,
    textAlign: 'center',
    maxWidth: 600,
  },
  neuroscienceCard: {
    padding: 48,
    borderRadius: 20,
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    width: '100%',
  },
  neuroscienceIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
  },
  neuroscienceTitle: {
    fontSize: 32,
    fontWeight: '700',
    marginBottom: 16,
    textAlign: 'center',
  },
  neuroscienceDescription: {
    fontSize: 18,
    lineHeight: 1.7,
    textAlign: 'center',
    marginBottom: 48,
    maxWidth: 600,
  },
  benefitsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    width: '100%',
    maxWidth: 650,
    alignItems: 'center',
  },
  benefitItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 16,
    width: '100%',
  },
  benefitContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  benefitTitle: {
    fontSize: 20,
    fontWeight: '600',
  },
  benefitDescription: {
    fontSize: 16,
    lineHeight: 1.7,
  },
  finalCta: {
    padding: 64,
    borderRadius: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    width: '100%',
  },
  finalCtaTitle: {
    fontSize: 36,
    fontWeight: '700',
    marginBottom: 16,
    textAlign: 'center',
  },
  finalCtaSubtitle: {
    fontSize: 20,
    marginBottom: 40,
    lineHeight: 1.6,
    textAlign: 'center',
  },
  finalCtaButton: {
    padding: 20,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: 220,
    minHeight: 56,
  },
  finalCtaButtonText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
    textAlign: 'center',
  },
  footer: {
    paddingTop: 60,
    paddingBottom: 40,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
    maxWidth: 800,
    width: '100%',
  },
  footerLinks: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  footerLink: {
    cursor: 'pointer',
  },
  footerLinkText: {
    fontSize: 14,
    fontWeight: '500',
  },
  footerSeparator: {
    fontSize: 14,
  },
  footerCopyright: {
    fontSize: 13,
    textAlign: 'center',
  },
});

export default LandingPage;
